/**
 * Utilidades da wiki no site do aluno (backlog#343): datas no idioma do site
 * (Intl nativo — o moment do bundle não carrega os locales), rótulo do prazo e
 * diff por palavras para o aviso de conflito.
 */
export function siteLocale() {
    const lang = (localStorage.getItem('lang') || document.documentElement.lang || 'pt-BR').replace('_', '-');
    return lang.toLowerCase().startsWith('pt') ? 'pt-BR' : lang;
}

const DAY = 86400000;

/** Data e hora curtas no idioma do site (ex.: 06/10/2026 09:39). */
export function formatDateTime(value) {
    if (!value) return '—';
    return new Intl.DateTimeFormat(siteLocale(), { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(value));
}

/** Só a data (ex.: 06/10/2026). */
export function formatDate(value) {
    if (!value) return '—';
    return new Intl.DateTimeFormat(siteLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(value));
}

/** Só a hora (ex.: 09:39). */
export function formatTime(value) {
    return new Intl.DateTimeFormat(siteLocale(), { hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(value));
}

/**
 * @param {{start_at?: string, end_at?: string}} wiki wiki
 * @returns {{key: string, days?: number, params?: Object}|null}
 */
export function deadlineLabel(wiki) {
    const now = Date.now();
    if (wiki.start_at && new Date(wiki.start_at).getTime() > now) {
        return { key: 'not_started', params: { date: formatDate(wiki.start_at) } };
    }
    if (!wiki.end_at) return null;
    const end = new Date(wiki.end_at).getTime();
    if (end < now) return { key: 'over' };
    const days = Math.ceil((end - now) / DAY);
    if (days <= 0) return { key: 'ends_today' };
    return { key: 'ends_in', days };
}

/** Data amigável: "hoje às 14:32", "ontem 10:05", ou data e hora. */
export function friendlyDate(value, t) {
    if (!value) return '—';
    const d = new Date(value);
    const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    const today = new Date();
    const yesterday = new Date(today.getTime() - DAY);
    if (sameDay(d, today)) return t('wiki.today_at', { time: formatTime(d) });
    if (sameDay(d, yesterday)) return `${t('wiki.yesterday')} ${formatTime(d)}`;
    return formatDateTime(d);
}

/** HTML → texto puro (para o diff do conflito). */
export function htmlToText(html) {
    const div = document.createElement('div');
    div.innerHTML = (html || '').replace(/<(br|\/p|\/div|\/li|\/h[1-6]|\/tr)[^>]*>/gi, '\n');
    return (div.textContent || '').replace(/[ \t]+/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
}

/** Diff por palavras (LCS) → [{type: equal|insert|delete, text}]. */
export function wordDiff(oldText, newText) {
    const tok = (s) => s.match(/\n|[^\s.,;:!?()]+|\s+|[.,;:!?()]/g) || [];
    let a = tok(oldText);
    let b = tok(newText);
    if (a.length * b.length > 4000000) { a = oldText.split(/(?<=\n)/); b = newText.split(/(?<=\n)/); }
    const n = a.length; const m = b.length;
    const table = new Array(n + 1);
    for (let i = n; i >= 0; i--) {
        const row = new Uint32Array(m + 1);
        for (let j = m - 1; j >= 0 && i < n; j--) {
            row[j] = a[i] === b[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], row[j + 1]);
        }
        table[i] = row;
    }
    const ops = [];
    let i = 0; let j = 0;
    const push = (type, text) => { const last = ops[ops.length - 1]; if (last && last.type === type) last.text += text; else ops.push({ type, text }); };
    while (i < n && j < m) {
        if (a[i] === b[j]) { push('equal', a[i]); i++; j++; } else if (table[i + 1][j] >= table[i][j + 1]) { push('delete', a[i++]); } else { push('insert', b[j++]); }
    }
    while (i < n) push('delete', a[i++]);
    while (j < m) push('insert', b[j++]);
    return ops;
}

/**
 * Rótulo do vínculo para o aluno: "Geral", "Plano: X" ou "Curso: X".
 *
 * @param {{relation_type: string, relation_name?: string}} wiki wiki
 * @param {Function} t função de tradução
 * @returns {string}
 */
export function relationLabel(wiki, t) {
    if (wiki.relation_type === 'plan') return t('wiki.relation.plan', { name: wiki.relation_name || '—' });
    if (wiki.relation_type === 'course') return t('wiki.relation.course', { name: wiki.relation_name || '—' });
    return t('wiki.relation.general');
}

/**
 * Rótulo da situação para o aluno (inclui "Abre em DD/MM/AAAA").
 *
 * @param {{status: string, start_at?: string}} wiki wiki
 * @param {Function} t função de tradução
 * @returns {string}
 */
export function statusLabel(wiki, t) {
    if (wiki.status === 'not_started') return t('wiki.status.not_started', { date: formatDate(wiki.start_at) });
    return t(`wiki.status.${wiki.status}`);
}

/** Tipos aceitos no seletor de arquivo. */
export const WIKI_FILE_ACCEPT = '.png,.jpg,.jpeg,.gif,.webp,.pdf,.pptx,.docx,.xlsx,image/png,image/jpeg,image/gif,image/webp,application/pdf';
